import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LegacyChainCanvas from '../components/LegacyChain/LegacyChainCanvas';
import LegacyChainNodePanel from '../components/LegacyChain/LegacyChainNodePanel';
import LegacyChainLegend from '../components/LegacyChain/LegacyChainLegend';
import LegacyChainStats from '../components/LegacyChain/LegacyChainStats';
import LegacyChainSync from '../components/LegacyChain/LegacyChainSync';
import { buildLegacyChainGraph, getMemorialColor } from '../services/legacyChainEngine';

const {
  FiGitBranch, FiSearch, FiX, FiFilter, FiInfo,
  FiUsers, FiLink, FiZap,
} = FiIcons;

const GENERATION_FILTERS = [
  { label: 'All', value: null },
  { label: 'Great-Grandparents', value: -2 },
  { label: 'Parents', value: -1 },
  { label: 'Core', value: 0 },
  { label: 'Children', value: 1 },
  { label: 'Grandchildren', value: 2 },
];

export default function LegacyChain() {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [genFilter, setGenFilter] = useState(null);
  const [clusterFilter, setClusterFilter] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [syncedMembers, setSyncedMembers] = useState([]);

  const {
    nodes: ALL_NODES,
    edges: ALL_EDGES,
    memorialClusters: ALL_CLUSTERS,
  } = useMemo(() => buildLegacyChainGraph(syncedMembers), [syncedMembers]);

  const filteredNodes = useMemo(() => {
    let ns = ALL_NODES;
    if (genFilter !== null) ns = ns.filter(n => n.generation === genFilter);
    if (clusterFilter) ns = ns.filter(n => n.memorialId === clusterFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      ns = ns.filter(n =>
        n.name.toLowerCase().includes(q) ||
        n.relation.toLowerCase().includes(q) ||
        n.memorialName.toLowerCase().includes(q)
      );
    }
    return ns;
  }, [ALL_NODES, genFilter, clusterFilter, search]);

  const filteredIds = useMemo(() => new Set(filteredNodes.map(n => n.id)), [filteredNodes]);
  const filteredEdges = useMemo(
    () => ALL_EDGES.filter(e => filteredIds.has(e.source) && filteredIds.has(e.target)),
    [ALL_EDGES, filteredIds]
  );

  const connectedNodes = useMemo(() => {
    if (!selected) return [];
    return ALL_EDGES
      .filter(e => e.source === selected.id || e.target === selected.id)
      .map(e => {
        const otherId = e.source === selected.id ? e.target : e.source;
        const node = ALL_NODES.find(n => n.id === otherId);
        return node ? { node, edge: e } : null;
      })
      .filter(Boolean);
  }, [selected, ALL_EDGES, ALL_NODES]);

  const handleSelect = useCallback(node => {
    setSelected(prev => prev?.id === node.id ? null : node);
  }, []);

  const crossEdgesCount = ALL_EDGES.filter(e => e.crossMemorial).length;

  return (
    <div className="pt-28 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-6 max-w-7xl">

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="bg-white rounded-[40px] p-8 md:p-12 border border-brand-dark shadow-sm overflow-hidden relative">
            <div className="absolute top-0 right-0 w-72 h-72 bg-accent-gold/5 rounded-full -mr-36 -mt-36 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/5 rounded-full -ml-24 -mb-24 blur-2xl pointer-events-none" />

            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-accent-navy to-purple-700 rounded-[22px] flex items-center justify-center shadow-xl">
                  <SafeIcon icon={FiGitBranch} className="w-8 h-8 text-accent-gold" />
                </div>
                <div>
                  <h1 className="text-4xl font-serif text-accent-navy">Legacy Chain</h1>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">
                    Visualising familial bonds across {ALL_CLUSTERS.length} memorials · {ALL_NODES.length} people · {crossEdgesCount} cross-memorial connections
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowInfo(v => !v)}
                  className="flex items-center gap-2 px-5 py-2.5 border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-all">
                  <SafeIcon icon={FiInfo} className="w-3.5 h-3.5" />
                  About
                </button>
                <Link to="/user/dashboard"
                  className="flex items-center gap-2 px-5 py-2.5 bg-accent-gold text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all shadow-md shadow-accent-gold/20">
                  <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" />
                  My Network
                </Link>
              </div>
            </div>

            <AnimatePresence>
              {showInfo && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-8 pt-8 border-t border-brand-light">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {[
                        { icon: FiGitBranch, title: 'Interactive Graph', desc: 'Pan, zoom, and click any node to explore their life story, relationships, and memorial connections.' },
                        { icon: FiLink, title: 'Cross-Memorial Links', desc: 'Gold dashed lines reveal shared surnames and generational ties across different memorial families.' },
                        { icon: FiZap, title: 'AI-Powered Discovery', desc: 'The Legacy Chain is enriched by the AI engine that surfaces hidden family connections across memorials.' },
                      ].map(item => (
                        <div key={item.title} className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-accent-navy/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <SafeIcon icon={item.icon} className="w-5 h-5 text-accent-navy" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-accent-navy mb-1">{item.title}</p>
                            <p className="text-[11px] text-text-muted font-light leading-relaxed">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <LegacyChainSync onSynced={setSyncedMembers} />

        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="mb-6">
          <LegacyChainStats nodes={ALL_NODES} edges={ALL_EDGES} memorialClusters={ALL_CLUSTERS} />
        </motion.div>

        {/* Filters */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="mb-6 flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-grow">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, relationship, or memorial…"
              className="w-full pl-11 pr-10 py-3 bg-white border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-colors" />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-accent-navy">
                <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Generation filter */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
            {GENERATION_FILTERS.map(f => (
              <button key={f.label} onClick={() => setGenFilter(f.value)}
                className={`px-3 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 whitespace-nowrap transition-all ${
                  genFilter === f.value
                    ? 'bg-accent-navy border-accent-navy text-white'
                    : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Cluster filter pills */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="flex gap-2 mb-6 flex-wrap">
          <button onClick={() => setClusterFilter(null)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
              !clusterFilter ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-navy'
            }`}>
            <SafeIcon icon={FiFilter} className="w-3 h-3" /> All Memorials
          </button>
          {ALL_CLUSTERS.map(cluster => {
            const active = clusterFilter === cluster.id;
            return (
              <button key={cluster.id} onClick={() => setClusterFilter(active ? null : cluster.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                  active ? 'text-white' : 'bg-white text-text-muted hover:border-opacity-70'
                }`}
                style={active
                  ? { background: cluster.color, borderColor: cluster.color }
                  : { borderColor: cluster.color + '60' }}>
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: cluster.color }} />
                {cluster.name.split(' ').slice(0, 2).join(' ')}
              </button>
            );
          })}
        </motion.div>

        {/* Main canvas */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="relative mb-8">
          <LegacyChainCanvas
            nodes={filteredNodes}
            edges={filteredEdges}
            memorialClusters={ALL_CLUSTERS}
            selectedNode={selected}
            onSelectNode={handleSelect}
          />
          {selected && (
            <LegacyChainNodePanel
              node={selected}
              connectedNodes={connectedNodes}
              onClose={() => setSelected(null)}
            />
          )}
        </motion.div>

        {/* Legend */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-white rounded-[28px] border border-brand-dark p-6 shadow-sm mb-8">
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-4 flex items-center gap-2">
            <SafeIcon icon={FiFilter} className="w-3 h-3" /> Graph Legend
          </p>
          <LegacyChainLegend />
        </motion.div>

        {/* Person list */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="bg-white rounded-[40px] border border-brand-dark p-8 shadow-sm">
          <h2 className="text-2xl font-serif text-accent-navy mb-6 flex items-center gap-3">
            <SafeIcon icon={FiUsers} className="text-accent-gold" />
            All People in the Chain
            <span className="text-sm font-sans font-normal text-text-muted">({filteredNodes.length})</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {filteredNodes.map(node => {
              const color = getMemorialColor(node.memorialId);
              const isActive = selected?.id === node.id;
              return (
                <motion.button
                  key={node.id}
                  layout
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelect(node)}
                  className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left transition-all ${
                    isActive
                      ? 'shadow-md'
                      : 'border-brand-dark bg-brand-light/50 hover:border-opacity-70'
                  }`}
                  style={isActive
                    ? { borderColor: color.primary, background: color.light }
                    : {}}
                >
                  <div className="relative flex-shrink-0">
                    {node.avatar ? (
                      <img src={node.avatar} alt={node.name}
                        className="w-11 h-11 rounded-xl object-cover border-2 border-white shadow-sm" />
                    ) : (
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center border-2 border-white shadow-sm"
                        style={{ background: color.primary }}>
                        <span className="text-white font-black text-xs">
                          {node.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                    {node.isDeceased && (
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center text-[8px]"
                        style={{ background: color.accent }}>
                        🕯
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-accent-navy text-sm truncate">{node.name}</p>
                    <p className="text-[9px] font-black uppercase tracking-widest truncate" style={{ color: color.primary }}>
                      {node.relation}
                    </p>
                    <p className="text-[9px] text-text-muted truncate">{node.memorialName.split(' ').slice(0, 3).join(' ')}</p>
                  </div>
                  {isActive && <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color.primary }} />}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}